/**
 * Index of an FK edge list by table name (either endpoint, lower-cased),
 * built for ONE synchronous pass over the list.
 *
 * FK applicability is checked once per (bound relation × candidate table) —
 * in `computeFkLinkedTableKeys` and every dialect's `emitFkJoinSnippets` — so
 * a linear filter of the whole FK list per check made a `JOIN ▮` keystroke
 * O(tables × bindings × FKs): 55–120 ms on a 600-table / 1,800-FK schema
 * (1.5.1 release-health F2). Those passes build this index once
 * (`makeApplicableFksFinder`) instead: O(FKs) to build, O(1) per lookup.
 *
 * Deliberately NOT cached across passes (e.g. on the array's identity): the
 * published MSSQL snapshot's `foreignKeys` array is refilled IN PLACE by the
 * phase-2 page sink (`mssqlSnapshotMerge.ts` `applyGlobalPage`), so a cached
 * index would serve deleted edges and miss new ones. Nothing can mutate the
 * array during the synchronous pass the index lives for.
 *
 * Each bucket keeps the edges in their original order, so filtering a bucket
 * gives the same result, in the same order, as filtering the whole list.
 */

import type { FkEdge } from '../../../core/engines';

import type { EndpointTable, FkEndpointMatcher } from './fkEndpointMatch';

const NO_EDGES: readonly FkEdge[] = [];

/** Returns a lookup of the edges touching a table (by name, case-insensitive). */
export function buildFkEdgeIndex(edges: readonly FkEdge[]): (tableName: string) => readonly FkEdge[] {
  const index = new Map<string, FkEdge[]>();
  const add = (key: string, fk: FkEdge) => {
    const bucket = index.get(key);
    if (bucket) bucket.push(fk);
    else index.set(key, [fk]);
  };
  for (const fk of edges) {
    const from = fk.fromTable.toLowerCase();
    const to = fk.toTable.toLowerCase();
    add(from, fk);
    if (to !== from) add(to, fk);
  }
  return (tableName) => index.get(tableName.toLowerCase()) ?? NO_EDGES;
}

/**
 * The edges touching a table by RELATION IDENTITY (`fkEndpointMatch.ts`):
 * each edge is filed under the snapshot tables its two endpoints name, so a
 * lookup returns only the edges an identity test can accept for that table —
 * the same edges, in the same order, as filtering {@link buildFkEdgeIndex}'s
 * name bucket by `match.endpointIs`. A name bucket spans schemas: with 1,000
 * tenant schemas each holding `orders → public.parents`, every `orders`
 * candidate re-tested all 1,000 edges (`tenantSchemaFkJoin.bench.ts`).
 */
export function buildFkEdgeIdentityIndex(
  edges: readonly FkEdge[],
  match: FkEndpointMatcher,
): (table: EndpointTable) => readonly FkEdge[] {
  // schema ('\u0001' for none) → name → edges, keeping the edge order.
  const index = new Map<string, Map<string, FkEdge[]>>();
  const add = (t: EndpointTable, fk: FkEdge) => {
    const schemaKey = t.schema ?? '\u0001';
    let byName = index.get(schemaKey);
    if (!byName) index.set(schemaKey, (byName = new Map()));
    const bucket = byName.get(t.name);
    // A self-reference (or two endpoints naming one table) files the edge once.
    if (!bucket) byName.set(t.name, [fk]);
    else if (bucket[bucket.length - 1] !== fk) bucket.push(fk);
  };
  for (const fk of edges) {
    for (const t of match.endpointTables(fk.fromSchema, fk.fromTable)) add(t, fk);
    for (const t of match.endpointTables(fk.toSchema, fk.toTable)) add(t, fk);
  }
  return (table) => index.get(table.schema ?? '\u0001')?.get(table.name) ?? NO_EDGES;
}
